'use client';

import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { FiX, FiClock, FiArrowRight } from 'react-icons/fi';
import Cookies from 'js-cookie';
import config from '../../config';
import { useLanguage } from '../../contexts/LanguageContext';
import PriceDisplay from '../common/PriceDisplay';
import ProductImage from '../common/ProductImage';
import { SandDecor, SAND_GRADIENT } from '../common/PageKit';
import { PharmacyCross } from './landingUi';
import { getProductImageUrl as getProductImageUrlUtil } from '../../utils/imageUtils';
import { CONSENT_RESOLVED_EVENT } from '../common/CookieConsent';
import { setClickSource } from '../../services/analytics/analyticsService';

// Remembers (by campaign id) that this visitor has already seen the popup this
// browser session. Stored in sessionStorage so it shows once per session and
// returns on the next session / new tab; a different active campaign within the
// same session still shows (keyed by campaign id).
const STORAGE_KEY = 'dialine.welcomeCampaign.dismissed';
// How many campaign products to preview inside the popup.
const PREVIEW_COUNT = 3;
// Small beat so the popup eases in over the painted page instead of blocking
// first paint.
const OPEN_DELAY_MS = 900;

/** Hex + 2-char alpha; null if hex isn't a clean 6-digit color. */
const withAlpha = (hex, aa) =>
  hex && /^#[0-9a-fA-F]{6}$/.test(hex) ? `${hex}${aa}` : null;

/**
 * Welcome popup that surfaces the featured active campaign the first time a
 * visitor sees it. Renders nothing when there is no campaign to promote.
 *
 * @param {object|null} campaign Featured campaign
 *   ({ _id, name, description, slug, accentColor, bannerImage, endsAt }).
 * @param {Array} [products] Campaign products; a few are previewed in the popup.
 */
export default function WelcomeCampaignModal({ campaign, products = [] }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  const campaignId = campaign?._id ? String(campaign._id) : null;

  useEffect(() => {
    if (!campaignId) return undefined;

    let dismissed = null;
    try {
      dismissed = sessionStorage.getItem(STORAGE_KEY);
    } catch {
      // sessionStorage unavailable (e.g. private mode) — treat as not dismissed.
    }
    if (dismissed === campaignId) return undefined;

    let timer = null;
    const openAfterDelay = () => {
      timer = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
    };

    // Cookies come first: if the visitor hasn't resolved the cookie consent
    // yet, wait for it so the two popups never clash. Returning visitors (who
    // already have a consent cookie) see the campaign right away, as before.
    if (Cookies.get('cookieConsent')) {
      openAfterDelay();
      return () => {
        if (timer) clearTimeout(timer);
      };
    }

    const onResolved = () => openAfterDelay();
    window.addEventListener(CONSENT_RESOLVED_EVENT, onResolved, { once: true });
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(CONSENT_RESOLVED_EVENT, onResolved);
    };
  }, [campaignId]);

  const dismiss = () => {
    setOpen(false);
    try {
      if (campaignId) sessionStorage.setItem(STORAGE_KEY, campaignId);
    } catch {
      // Ignore write failures; worst case the popup shows again next visit.
    }
  };

  const handleShop = () => {
    dismiss();
    if (campaign?.slug) navigate(`/products?campaign=${campaign.slug}`);
  };

  if (!campaign) return null;

  const accent = campaign.accentColor || 'rgb(var(--brand))';
  const bannerSrc = campaign.bannerImage
    ? `${config.imageBaseURL}${campaign.bannerImage}`
    : null;

  // Same "ends soon" computation CampaignSection uses.
  const endsAt = campaign.endsAt ? new Date(campaign.endsAt) : null;
  const daysUntilEnd = endsAt
    ? Math.max(0, Math.ceil((endsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;
  const showEndsSoon = daysUntilEnd != null && daysUntilEnd <= 14;
  const endsLabel =
    daysUntilEnd === 0
      ? t('home.discount.endsToday') || 'Ends today'
      : daysUntilEnd === 1
        ? t('home.discount.endsTomorrow') || 'Ends tomorrow'
        : `${t('home.discount.endsIn') || 'Ends in'} ${daysUntilEnd} ${t('home.discount.days') || 'days'}`;

  // A few products to tease inside the popup. Keep only those with a usable
  // article so cards never render empty.
  const previewProducts = (Array.isArray(products) ? products : [])
    .filter((p) => p?.articles?.[0])
    .slice(0, PREVIEW_COUNT);

  const productTitle = (article) => {
    const folder = article.consumerFolder;
    const fromFolder = Array.isArray(folder) ? folder[0]?.title : folder?.title;
    return fromFolder || article.description?.longDescription || '';
  };

  const openProduct = (pharmacode) => {
    setClickSource('campaign');
    dismiss();
    navigate(`/product/${pharmacode}`);
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      {/* Above the help chat panel (z-[9999]) — a campaign popup must sit on top of the support widget, not behind it. */}
      <Dialog as="div" className="relative z-[10000]" onClose={dismiss}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/45 backdrop-blur-sm" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 z-[10000] flex items-end justify-center overflow-y-auto p-3 sm:items-center sm:p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-6 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-6 sm:translate-y-0 sm:scale-95"
          >
            {/* Sand card, like the rest of the site: dot grid, a blush corner
                and a faint cross; the banner sits inside it as a framed photo. */}
            <Dialog.Panel
              className="relative w-full overflow-hidden rounded-[2rem] p-2.5 ring-1 ring-black/[0.05] shadow-[0_40px_90px_-30px_rgba(17,24,39,0.55)] sm:max-w-lg sm:p-3"
              style={{ background: SAND_GRADIENT }}
            >
              <SandDecor radius="rounded-[2rem]" crossClassName="absolute -bottom-[20%] -right-[12%] h-[70%] w-auto rotate-[-10deg]" />

              {/* Close */}
              <button
                type="button"
                onClick={dismiss}
                className="absolute right-5 top-5 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-gray-600 shadow-[0_8px_20px_-10px_rgba(17,24,39,0.6)] ring-1 ring-black/[0.05] backdrop-blur transition-colors hover:text-gray-900 sm:right-6 sm:top-6"
                aria-label={t('home.welcomeCampaign.close') || 'Close'}
              >
                <FiX className="h-4 w-4" />
              </button>

              {/* Header — banner image (clickable) or accent fallback */}
              <button
                type="button"
                onClick={handleShop}
                className="relative block w-full overflow-hidden rounded-[1.5rem] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                aria-label={`${t('home.discount.viewAllCampaign') || 'View all'} ${campaign.name}`}
              >
                {bannerSrc ? (
                  <div className="h-36 w-full overflow-hidden bg-[#EFEBE7] sm:h-44">
                    <img
                      src={bannerSrc}
                      alt={campaign.name}
                      className="h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-[1.04]"
                      loading="eager"
                      decoding="async"
                    />
                  </div>
                ) : (
                  <div
                    className="relative h-24 w-full overflow-hidden sm:h-28"
                    style={{ background: `linear-gradient(135deg, ${accent} 0%, ${withAlpha(accent, 'cc') || accent} 100%)` }}
                  >
                    <PharmacyCross className="absolute -right-4 -top-6 h-40 w-auto rotate-[-10deg]" style={{ color: '#fff', opacity: 0.14 }} />
                  </div>
                )}
              </button>

              {/* Body */}
              <div className="relative px-3 pb-3 pt-5 sm:px-5 sm:pb-5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white py-1 pl-2.5 pr-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500 ring-1 ring-inset ring-black/[0.07]">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full" style={{ background: accent }} />
                    {t('home.discount.campaignTag') || 'Campaign'}
                  </span>
                  {showEndsSoon && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-hover">
                      <FiClock className="h-3 w-3" aria-hidden="true" />
                      {endsLabel}
                    </span>
                  )}
                </div>

                <Dialog.Title
                  as="h2"
                  className="mt-3 text-[clamp(1.6rem,5vw,2.1rem)] font-extrabold leading-[1.05] tracking-[-0.04em] text-gray-900"
                >
                  {campaign.name}
                </Dialog.Title>

                {campaign.description && (
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-gray-500 sm:text-[15px]">
                    {campaign.description}
                  </p>
                )}

                {/* Product teaser — a few campaign products, each tappable. */}
                {previewProducts.length > 0 && (
                  <div className="mt-5 grid grid-cols-3 gap-2 sm:gap-2.5">
                    {previewProducts.map((product, i) => {
                      const article = product.articles[0];
                      const imageUrl = getProductImageUrlUtil(article, 'F');
                      const title = productTitle(article);
                      return (
                        <button
                          key={article.pharmacode || product._id || i}
                          type="button"
                          onClick={() => openProduct(article.pharmacode)}
                          className="group flex flex-col overflow-hidden rounded-[1.1rem] bg-white p-1.5 text-left ring-1 ring-black/[0.05] shadow-[0_14px_30px_-24px_rgba(17,24,39,0.5)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-24px_rgb(var(--brand)/0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                          aria-label={title}
                        >
                          <div className="relative w-full overflow-hidden rounded-[0.8rem] bg-gradient-to-b from-[#FAF8F6] to-[#EFEBE7] [aspect-ratio:1]">
                            {imageUrl && (
                              <ProductImage src={imageUrl} alt={title} pad={0.12} className="group-hover:[--img-hover:1.06]" />
                            )}
                          </div>
                          <div className="px-1 pb-1 pt-2">
                            <p className="line-clamp-2 min-h-[2rem] text-[11px] font-bold leading-snug text-gray-900 transition-colors group-hover:text-brand">
                              {title}
                            </p>
                            <div className="mt-1 text-xs">
                              <PriceDisplay
                                article={article}
                                variant="compact"
                                fallbackKey="home.discount.priceOnRequest"
                                showVatHint={false}
                                showBadge={false}
                              />
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="mt-5 flex flex-col-reverse items-center gap-2 sm:flex-row sm:justify-between">
                  <button
                    type="button"
                    onClick={dismiss}
                    className="rounded-full px-4 py-2.5 text-sm font-semibold text-gray-500 transition-colors hover:text-gray-900"
                  >
                    {t('home.welcomeCampaign.noThanks') || 'No thanks'}
                  </button>
                  <button
                    type="button"
                    onClick={handleShop}
                    className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-6 py-3.5 text-sm font-semibold text-white shadow-[0_12px_28px_-12px_rgb(var(--brand)/0.6)] transition-colors hover:bg-brand-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 sm:w-auto"
                  >
                    {t('home.welcomeCampaign.shopOffers') || 'Shop the offers'}
                    <FiArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
